import { Link } from "react-router-dom";

// "What changed since your review", a light second-pass sheet for the design
// team, so they score the fixes instead of re-finding the same issues.

const FIXED = [
  {
    id: "orientation",
    score: "Post-sync guidance · summary paragraph",
    said: "\u201cAfter Sync My Sheet, some guidance would be helpful. A few of the steps aren't very clear.\u201d",
    did: [
      "A \u201cStart here\u201d checklist now appears once setup is done: log a day \u2192 tag a day off \u2192 close your first relay \u2192 open your sheet. It ticks itself off and disappears when you're through it.",
      "A \u201cHow the race works\u201d card teaches the system on first run and stays available under More \u2192 How the race works.",
    ],
    where: "/app (signed in)",
  },
  {
    id: "metaphor",
    score: "Relay metaphor \u2014 1/5",
    said: "\u201cA bit confusing.\u201d",
    did: [
      "Plain-language containers, everywhere the label appears: a relay is one earning week \u00b7 a Sprint is four relays \u00b7 a race segment is the current U.S. estimated-tax period \u00b7 a Quarterly Tax Day closes each segment \u00b7 the full race is 52 weekly relays.",
      "The dashboard now reads \u201cRelay 3 of 4 in your Sprint \u00b7 this week: \u2026\u201d with the current tax period and next quarterly tax day underneath, so you always know which layer you're looking at.",
      "A seven-day track graphic (day chips \u2192 checkered finish) shows what one relay is, on the dashboard and on the landing page.",
    ],
    where: "/app and /beta",
  },
  {
    id: "one-system",
    score: "\u201cDoes app + sheet feel like ONE system?\u201d \u2014 1/5",
    said: "\u201cNo.\u201d",
    did: [
      "Shared framing in both places: \u201cYou log in the app. Your Google Sheet keeps the season record.\u201d",
      "The sheet now uses the app's palette \u2014 cream page, indigo structure \u2014 and the segment colours match the relay batons in both.",
    ],
    where: "/app + your Google Sheet",
  },
  {
    id: "library",
    score: "Resource Library \u2014 1/5",
    said: "\u201cAll the tools mentioned look like text boxes.\u201d",
    did: [
      "Rebuilt as a library: pick count, category rail, and a card per resource with an icon tile, category label, short description and its source domain.",
      "Copy tightened \u2014 warm, not jokey.",
    ],
    where: "/app/resources (Season Pass)",
  },
  {
    id: "sheet",
    score: "Google Sheet organisation \u2014 1/5",
    said: "\u201cDesign + colour usage looks overwhelming.\u201d",
    did: [
      "The dark indigo/teal canvas is now light paper, with one strong title band per tab and cream section headers.",
      "The segment band at the top of each month tab kept its full-strength baton colour \u2014 it's wayfinding: yellow Jan 1\u2013Mar 31, red Apr 1\u2013May 31, teal Jun 1\u2013Aug 31, indigo Sep 1\u2013Dec 31 (the uneven U.S. estimated-tax periods).",
      "Structural gold borders softened to a warm neutral and every heavy border dropped to a hairline. Input cells are warm cream; anything the sheet calculates stays white.",
      "Every tab carries a colour-key note on A1, and the first inputs carry \u201cstart here\u201d notes.",
    ],
    where: "Your Google Sheet",
  },
  {
    id: "monthly-journal",
    score: "Journal + Monthly Wrap-Up on desktop \u2014 2/5",
    said: "\u201cNot very clear.\u201d",
    did: [
      "Monthly Wrap-Up gained the desktop sidebar, a title and a line explaining that a month holds four or five relays, with totals and the relay-by-relay list side by side.",
      "The Journal is two columns on desktop \u2014 a sticky composer on the left, entries on the right.",
    ],
    where: "/app/monthly and /app/journal",
  },
  {
    id: "pto",
    score: "Time Off / PTO on mobile \u2014 2/5",
    said: "Flow felt heavy on a phone.",
    did: [
      "The day panel is one job again: tag the day. Fixing a dollar amount and taking the whole week off are now one-tap disclosures instead of always-open blocks.",
      "Tapping a date scrolls its panel into view, and the PTO bank explainer is behind \u201cWhat these numbers mean\u201d.",
    ],
    where: "/app/timeoff on a phone",
  },
  {
    id: "nav",
    score: "Mobile bottom nav \u2014 3/5",
    said: "\u201cClear what each icon does?\u201d",
    did: ["Bigger icons with readable labels, in plainer words: Today \u00b7 Calc \u00b7 Scores \u00b7 Journal \u00b7 Library \u00b7 More."],
    where: "Any /app screen on a phone",
  },
  {
    id: "landing",
    score: "Landing page \u2014 who it's for 3/5, Sprint clarity 3/5, polish 2/5",
    said: "Audience and offer weren't obvious enough above the fold.",
    did: [
      "The hero opens with the audience \u2014 \u201cFor freelancers, gig workers & 1099 contractors\u201d \u2014 and states the Sprint plainly: 28 days free, card on file charged $0, then $19/mo or $119/yr, sheet stays yours.",
      "A new \u201cA relay is one earning week\u201d section sits right under the hero, and the palette was calmed down.",
    ],
    where: "/beta",
  },
  {
    id: "pitstop",
    score: "Sprint tone / surprises",
    said: "Recalibration could catch people off guard.",
    did: ["A gentle countdown now names the next Quarterly Tax Day and how many days away it is, on the dashboard."],
    where: "/app",
  },
  {
    id: "settings",
    score: "New since your review \u2014 not previously scored",
    said: "No reviewer had a single place to manage reminders or billing.",
    did: [
      "A Settings page collects everything account-shaped: log reminders (pick your own time, in 15-minute steps, in your own time zone), a Sunday close-out toggle, an email fallback, plus plan, billing history, payment method and cancellation.",
      "Reminders never fire on a day you already logged, or on a day/week tagged off, sick or PTO.",
      "Card details, receipts and payment-method changes all happen on Stripe\u2019s hosted pages \u2014 Payday Relay never sees or stores a card.",
    ],
    where: "/app/settings (sidebar on desktop, More \u2192 Settings on a phone)",
  },
];

const NOT_YET = [
  ["Locked sections during the Sprint \u2014 2/5", "Staying as-is by design: Sprint-only features unlock when someone holds a paid Season Pass, not at a relay count. Notes on how the locked cards are worded are still welcome."],
  ["Dark mode \u2014 3/5", "Removed from the scorecard: Payday Relay is staying light-mode only, on purpose."],
  ["Sidebar stickiness \u2014 2/5", "The sidebar is sticky, and the Monthly Wrap-Up page that was missing one now has it. Worth a re-check on your setup."],
];

export default function ReviewerChangesPage() {
  return (
    <div className="min-h-screen" style={{ background: "var(--pr-bg)" }} data-testid="reviewer-changes-page">
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-14 sm:py-20">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] mb-3" style={{ color: "var(--pr-muted)" }}>
          Second pass
        </p>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl mb-4" style={{ fontFamily: "'Fraunces', Georgia, serif", color: "var(--pr-ink)" }}>
          What changed since your review
        </h1>
        <p className="text-base leading-relaxed mb-3" style={{ color: "var(--pr-ink2)" }}>
          Thank you, the review was genuinely useful. Trust, checkout and sync scored well; orientation and
          presentation didn&rsquo;t. Everything below is what came out of that. Please score the fixes rather than
          re-finding the originals.
        </p>
        <p className="text-sm leading-relaxed mb-10" style={{ color: "var(--pr-muted)" }}>
          Roughly 15 minutes. Sign in, walk the eleven items below in order, then use the same scorecard.{" "}          <Link to="/reviewer-feedback" className="underline" style={{ color: "var(--pr-ink)" }} data-testid="reviewer-changes-feedback-link">
            Open the scorecard
          </Link>{" "}
          ·{" "}
          <Link to="/reviewer-guide" className="underline" style={{ color: "var(--pr-ink)" }} data-testid="reviewer-changes-guide-link">
            Full reviewer guide
          </Link>
        </p>

        <ol className="space-y-5">
          {FIXED.map((item, i) => (
            <li key={item.id} className="rounded-2xl p-5 sm:p-6 pr-card" data-testid={`reviewer-change-${item.id}`}>
              <div className="flex items-baseline gap-3 mb-1.5">
                <span className="text-xs font-bold" style={{ color: "var(--pr-muted)" }}>{String(i + 1).padStart(2, "0")}</span>
                <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: "var(--pr-muted)" }}>{item.score}</p>
              </div>
              <p className="text-sm italic mb-3" style={{ color: "var(--pr-ink2)" }}>{item.said}</p>
              <ul className="space-y-2 mb-3">
                {item.did.map((line) => (
                  <li key={line} className="text-sm leading-relaxed flex gap-2" style={{ color: "var(--pr-ink)" }}>
                    <span aria-hidden style={{ color: "var(--pr-green)" }}>&#10003;</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[11px]" style={{ color: "var(--pr-muted)" }}>Look here: {item.where}</p>
            </li>
          ))}
        </ol>

        <h2 className="text-lg sm:text-xl mt-12 mb-3" style={{ fontFamily: "'Fraunces', Georgia, serif", color: "var(--pr-ink)" }}>
          Deliberately not changed yet
        </h2>
        <ul className="space-y-3 mb-12">
          {NOT_YET.map(([label, why]) => (
            <li key={label} className="text-sm leading-relaxed" style={{ color: "var(--pr-ink2)" }} data-testid="reviewer-change-pending">
              <b style={{ color: "var(--pr-ink)" }}>{label}</b>, {why}
            </li>
          ))}
        </ul>

        <div className="rounded-2xl p-5 sm:p-6 pr-card">
          <p className="text-sm font-bold mb-1" style={{ color: "var(--pr-ink)" }}>One ask for this pass</p>
          <p className="text-sm leading-relaxed" style={{ color: "var(--pr-ink2)" }}>
            After the walkthrough, answer this in a sentence: <i>could you explain to a friend what a relay, a
            Sprint and a race segment are?</i> If yes, the orientation work landed.
          </p>
          <Link
            to="/reviewer-feedback"
            className="inline-block mt-4 px-6 py-3 rounded-full font-bold text-sm pr-btn-primary"
            data-testid="reviewer-changes-cta"
          >
            Score the second pass →
          </Link>
        </div>
      </div>
    </div>
  );
}
